import { format } from 'date-fns';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../lib/dateLocale';
import { parseToDate, safeFormatDate } from '../../../lib/utils';
import {
    SttDialog,
    SttFormDate,
    SttFormSelect,
    SttFormText,
    SttFormTextarea,
    SttFormTime,
    SttSelectFacility,
    SttSelectWeekdays,
} from '../../../sporttia/all';
import { useCrud } from '../../../lib/hooks';
import { PERMISSIONS } from '../../../lib/permissions';

type Facility = {
    id?: string | number;
    name?: string;
};

export type Period = {
    id?: string | number;
    name: string;
    notes?: string;
    idFacility?: string | number;
    facility?: Facility;
    status: string;
    ini: string;
    end: string;
    weekdays?: number[];
};

type Event = {
    id: string | number;
    name: string;
};

type Props = {
    item: Period;
    event?: Event;
    onClose: () => void;
    onSave: (period: Period) => void;
    onDelete: (period: Period) => void;
};

export default function ModalPeriod({
    item,
    onClose,
    onSave,
    onDelete,
}: Props): React.ReactElement {
    const [period, , setProperty] = useCrud<Period>(item);

    const { t } = useTranslation();

    const locale = getDateLocale();

    let subtitle: string;
    if (item.id) {
        const dayName = safeFormatDate(period.ini, 'EEEE', { locale });
        const dateStr = safeFormatDate(period.ini, 'd MMM yyyy', { locale });
        const iniTime = safeFormatDate(period.ini, 'HH:mm');
        const endTime = safeFormatDate(period.end, 'HH:mm');
        subtitle = `${period.name}, ${dayName}, ${dateStr}, ${iniTime} - ${endTime}`;
    } else {
        subtitle = `${t('NewPeriod')}, ${period.name}`;
    }

    function setDate({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void {
        if (!name) return;
        const currentValue = period[name as keyof Period] as string;
        const currentDate = parseToDate(currentValue);
        const newDate = parseToDate(value as string);
        if (!currentDate || !newDate) return;
        setProperty({
            name: name as keyof Period,
            value: `${format(newDate, 'yyyy-MM-dd')}T${format(
                currentDate,
                'HH:mm:ss',
            )}`,
        });
    }

    function setTime({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void {
        if (!name) return;
        const currentValue = period[name as keyof Period] as string;
        const currentDate = parseToDate(currentValue);
        if (!currentDate) return;
        setProperty({
            name: name as keyof Period,
            value: `${format(currentDate, 'yyyy-MM-dd')}T${value}:00`,
        });
    }

    return (
        <SttDialog
            open
            title={t('Period')}
            subtitle={subtitle}
            onClose={onClose}
            maxWidth="sm"
            content={
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <SttFormText
                            caption={t('Name')}
                            name="name"
                            defVal={period.name}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Period,
                                    value: value as string,
                                })
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <SttFormTextarea
                            caption={t('Notes')}
                            name="notes"
                            defVal={period.notes}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Period,
                                    value: value as string,
                                })
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                        <SttSelectFacility
                            caption={t('Facility')}
                            defVal={
                                (period?.facility?.id ||
                                    period?.idFacility) as number
                            }
                            onChange={(facility: Facility | null) =>
                                setProperty({
                                    name: 'idFacility',
                                    value: facility?.id,
                                })
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <SttFormSelect
                            caption={t('Status')}
                            name="status"
                            defVal={period.status}
                            options={[
                                {
                                    caption: t('Active'),
                                    value: 'ACTIVE',
                                },
                                {
                                    caption: t('Inactive'),
                                    value: 'INACTIVE',
                                },
                            ]}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Period,
                                    value: value as string,
                                })
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SttFormDate
                            disabled={!!item.id}
                            caption={t('Date')}
                            name="ini"
                            defVal={period.ini}
                            onChange={setDate}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SttFormDate
                            disabled={!!item.id}
                            caption={t('End')}
                            name="end"
                            defVal={period.end}
                            onChange={setDate}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SttFormTime
                            disabled={!!item.id}
                            caption={t('Begin')}
                            defVal={safeFormatDate(period.ini, 'HH:mm')}
                            name="ini"
                            onChange={setTime}
                            interval={5}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SttFormTime
                            disabled={!!item.id}
                            caption={t('Until')}
                            defVal={safeFormatDate(period.end, 'HH:mm')}
                            name="end"
                            onChange={setTime}
                            interval={5}
                        />
                    </Grid>
                    {!item.id && (
                        <Grid size={{ xs: 12 }}>
                            <SttSelectWeekdays
                                name="weekdays"
                                selected={period.weekdays}
                                onChange={({ name, value }) =>
                                    setProperty({
                                        name: name as keyof Period,
                                        value: value as number[],
                                    })
                                }
                            />
                        </Grid>
                    )}
                </Grid>
            }
            buttons={[
                {
                    type: 'accept',
                    onClick: () => onSave(period),
                    permission: PERMISSIONS.EVENT.MODIFY,
                },
                period.id
                    ? {
                          type: 'delete',
                          onClick: () => onDelete(period),
                          permission: PERMISSIONS.EVENT.MODIFY,
                      }
                    : false,
                {
                    type: 'cancel',
                    onClick: onClose,
                },
            ]}
        />
    );
}
